import React, { useState, useEffect } from 'react';
import { Template, TemplateFormData } from '../types/template';
import { Category } from '../types/category';
import { toast } from "sonner";

interface TemplateFormProps {
  onSubmit: (data: TemplateFormData) => void;
  onCancel: () => void;
  loading: boolean;
  categories: Category[];
  initialData?: Template | null;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  categories,
  initialData,
}) => {
  const [formData, setFormData] = useState<TemplateFormData>({
    type: '',
    cat_id: '',
    category: '',
    subcategory: '',
    ai_model: '',
    prompt: '',
    output: null,
  });

  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    if (initialData) {
      const category = categories.find(cat => String(cat.id) === String(initialData.cat_id));
      setFormData({
        type: initialData.type || '',
        cat_id: String(initialData.cat_id || ''),
        category: category?.category || '',
        subcategory: initialData.subcategory || '',
        ai_model: initialData.ai_model || '',
        prompt: initialData.prompt || '',
        output: null,
      });
      if (initialData.output && Array.isArray(initialData.output)) {
        setPreviews(initialData.output);
      }
    }
  }, [initialData, categories]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    
    if (name === 'category') {
      const selectedCategory = categories.find(cat => cat.category === value);
      if (selectedCategory && selectedCategory.id) {
        setFormData((prev) => ({ 
          ...prev, 
          category: selectedCategory.category, 
          cat_id: String(selectedCategory.id) 
        }));
      } else {
        setFormData((prev) => ({ 
          ...prev, 
          category: value,
          cat_id: '' 
        }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = e.target.files;
    if (newFiles && newFiles.length > 0) {
      const newFileArray = Array.from(newFiles);
      
      const existingFiles = formData.output ? Array.from(formData.output) : [];
      
      const combinedFiles = [...existingFiles, ...newFileArray];
      
      const dataTransfer = new DataTransfer();
      combinedFiles.forEach(file => dataTransfer.items.add(file));
      
      setFormData((prev) => ({ ...prev, output: dataTransfer.files }));
      
      const previewUrls: string[] = [...previews];
      
      let loadedCount = 0;
      newFileArray.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previewUrls.push(reader.result as string);
          loadedCount++;
          if (loadedCount === newFileArray.length) {
            setPreviews(previewUrls);
          }
        };
        reader.readAsDataURL(file);
      });
    }
    
    e.target.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!initialData && (!formData.output || formData.output.length === 0)) {
      toast.error('Please select at least one image');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-semibold mb-6">
        {initialData ? 'Edit Template' : 'Create Template'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            TYPE *
          </label>
          <input
            type="text"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CATEGORY *
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent bg-white"
          >
            <option value="">Select a category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.category}>
                {category.category}
              </option>
            ))}
          </select>
          {categories.length === 0 && (
            <p className="mt-1 text-sm text-gray-500">
              No categories available. Please create a category first.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SUBCATEGORY *
          </label>
          <input
            type="text"
            name="subcategory"
            value={formData.subcategory}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter subcategory"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            AI MODEL *
          </label>
          <select
            name="ai_model"
            value={formData.ai_model}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent bg-white"
          >
            <option value="">Select an AI model</option>
            <option value="Nano Banana (Google)">Nano Banana (Google)</option>
            <option value="GPT Image 1">GPT Image 1</option>
            <option value="Flux Kontext Pro">Flux Kontext Pro</option>
            <option value="Flux Kontext Max">Flux Kontext Max</option>
            <option value="Seedream 4">Seedream 4</option>
            <option value="Seededit V3">Seededit V3</option>
            <option value="Qwen Image Edit Plus">Qwen Image Edit Plus</option>
            <option value="Reve/ edit">Reve/ edit</option>
            <option value="Reve/ remix">Reve/ remix</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PROMPT *
          </label>
          <textarea
            name="prompt"
            value={formData.prompt}
            onChange={handleChange}
            required
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            REFERENCE IMAGES - Multiple selection allowed
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
          />
          {initialData && (
            <p className="mt-1 text-sm text-gray-500">
              Leave empty to keep current images, or select new images to replace them
            </p>
          )}
          {previews.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">
                  {previews.length} image{previews.length !== 1 ? 's' : ''} selected
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, output: null }));
                    setPreviews([]);
                  }}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Clear All
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {previews.map((previewUrl, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={previewUrl}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-300 hover:border-berrys-orange transition-colors"
                    />
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                      {index + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newPreviews = previews.filter((_, i) => i !== index);
                        setPreviews(newPreviews);
                        
                        if (formData.output) {
                          const filesArray = Array.from(formData.output);
                          const newFiles = filesArray.filter((_, i) => i !== index);
                          const dataTransfer = new DataTransfer();
                          newFiles.forEach(file => dataTransfer.items.add(file));
                          setFormData((prev) => ({ ...prev, output: dataTransfer.files }));
                        }
                      }}
                      className="absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-4 pt-4">
          
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading
              ? initialData
                ? 'Updating...'
                : 'Creating...'
              : initialData
              ? 'Update Template'
              : 'Create Template'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TemplateForm;

